import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Pencil, Monitor, BarChart3, Eye, X, Copy, Check, 
  Trash2, Ban, Calendar, Video, AlertCircle, ChevronRight, Database, Server, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents, mockChatMessages, mockLiveStats, mockDrafts } from '@/lib/mockData';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { getEvents, didNeonFail, getNeonErrorMessage } from '@/lib/api';
import { getDataSource } from '@/lib/api/config';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    not_started: { label: 'Not Started', className: 'bg-gray-500/20 text-gray-400' },
    in_progress: { label: 'In Progress', className: 'bg-green-500/20 text-green-400' },
    done: { label: 'Done', className: 'bg-blue-500/20 text-blue-400' },
    started: { label: 'Started', className: 'bg-yellow-500/20 text-yellow-400' }
  };
  const config = statusConfig[status] || statusConfig.not_started;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.className}`}>
      {config.label}
    </span>
  );
};

// Livestream badge component
const LivestreamBadge = ({ enabled }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
    enabled ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
  }`}>
    {enabled ? 'Enabled' : 'Disabled'}
  </span>
);

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button 
      onClick={handleCopy}
      className="p-2 hover:bg-secondary rounded-lg transition-colors flex-shrink-0"
      data-testid="copy-button"
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
    </button>
  );
};

// Edit Event Sheet Component - Full screen on mobile
const EditEventSheet = ({ event, isOpen, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    title: event?.title || '',
    date: event?.date?.split('T')[0] || '',
    livestreamEnabled: event?.livestreamEnabled || false,
    generalPrice: event?.ticketTypes?.[0]?.price || 50,
    vipPrice: event?.ticketTypes?.[1]?.price || 150,
    livestreamPrice: event?.ticketTypes?.[2]?.price || 25,
    streamName: event?.streamConfig?.streamName || '',
    latencyMode: event?.streamConfig?.latencyMode || 'low',
    dvrWindow: event?.streamConfig?.dvrWindow || 30,
    playbackPolicy: event?.streamConfig?.playbackPolicy || 'public',
    autoRecord: event?.streamConfig?.autoRecord || false,
    ingestUrl: event?.streamConfig?.ingestUrl || '',
    streamKey: event?.streamConfig?.streamKey || '',
    playbackUrl: event?.streamConfig?.playbackUrl || '',
    streamStatus: event?.streamConfig?.streamStatus || 'inactive'
  });

  if (!isOpen || !event) return null;

  const handleSave = () => {
    onSave(formData);
    toast.success('Event edited successfully');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="edit-event-sheet">
      {/* Backdrop - visible on desktop */}
      <div className="absolute inset-0 bg-black/60 hidden lg:block" onClick={onClose} />
      
      {/* Sheet - Full screen on mobile, side panel on desktop */}
      <div className="relative w-full lg:max-w-lg bg-card lg:border-l border-border h-full overflow-hidden flex flex-col animate-in slide-in-from-right lg:slide-in-from-right slide-in-from-bottom">
        {/* Header */}
        <div className="bg-card border-b border-border p-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg" data-testid="close-edit-sheet">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
          <h2 className="text-lg font-semibold text-white">Edit Event</h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border flex-shrink-0">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'details' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
            data-testid="tab-details"
          >
            Event Details
          </button>
          <button
            onClick={() => setActiveTab('streaming')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'streaming' 
                ? 'text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-white'
            }`}
            data-testid="tab-streaming"
          >
            Streaming Config
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-5 space-y-5">
          {activeTab === 'details' ? (
            <>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Event Name</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-secondary border-border"
                  data-testid="input-event-name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Event Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-secondary border-border"
                  data-testid="input-event-date"
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-sm text-white">Enable Livestream</label>
                <Switch
                  checked={formData.livestreamEnabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, livestreamEnabled: checked })}
                  data-testid="switch-livestream"
                />
              </div>

              <div className="space-y-4 pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-white">Ticket Prices</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">General</label>
                    <Input
                      type="number"
                      value={formData.generalPrice}
                      onChange={(e) => setFormData({ ...formData, generalPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">VIP</label>
                    <Input
                      type="number"
                      value={formData.vipPrice}
                      onChange={(e) => setFormData({ ...formData, vipPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Livestream</label>
                    <Input
                      type="number"
                      value={formData.livestreamPrice}
                      onChange={(e) => setFormData({ ...formData, livestreamPrice: e.target.value })}
                      className="bg-secondary border-border"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-white">Event Images</h3>
                <div className="flex gap-2 flex-wrap">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden">
                    <img src={event.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden">
                    <img src={event.coverImage} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button className="w-16 h-16 lg:w-20 lg:h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Stream Name</label>
                <Input
                  value={formData.streamName}
                  onChange={(e) => setFormData({ ...formData, streamName: e.target.value })}
                  className="bg-secondary border-border"
                  data-testid="input-stream-name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Latency Mode</label>
                <select
                  value={formData.latencyMode}
                  onChange={(e) => setFormData({ ...formData, latencyMode: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-white"
                  data-testid="select-latency"
                >
                  <option value="low">Low Latency</option>
                  <option value="normal">Normal</option>
                  <option value="high">High Quality</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">DVR Window (minutes)</label>
                <Input
                  type="number"
                  value={formData.dvrWindow}
                  onChange={(e) => setFormData({ ...formData, dvrWindow: e.target.value })}
                  className="bg-secondary border-border"
                  data-testid="input-dvr"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Playback Policy</label>
                <select
                  value={formData.playbackPolicy}
                  onChange={(e) => setFormData({ ...formData, playbackPolicy: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-white"
                  data-testid="select-policy"
                >
                  <option value="public">Public</option>
                  <option value="signed">Signed URL</option>
                  <option value="private">Private</option>
                </select>
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-sm text-white">Auto Record</label>
                <Switch
                  checked={formData.autoRecord}
                  onCheckedChange={(checked) => setFormData({ ...formData, autoRecord: checked })}
                  data-testid="switch-record"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Ingest URL</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.ingestUrl}
                    readOnly
                    className="bg-secondary border-border flex-1 font-mono text-xs"
                  />
                  <CopyButton text={formData.ingestUrl} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Stream Key</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.streamKey}
                    readOnly
                    className="bg-secondary border-border flex-1 font-mono text-xs"
                  />
                  <CopyButton text={formData.streamKey} />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Playback URL</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.playbackUrl}
                    readOnly
                    className="bg-secondary border-border flex-1 font-mono text-xs"
                  />
                  <CopyButton text={formData.playbackUrl} />
                </div>
              </div>

              <div className="flex items-center justify-between py-2 border-t border-border mt-4">
                <label className="text-sm text-white">Stream Status</label>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  formData.streamStatus === 'active' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {formData.streamStatus === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer - Sticky at bottom */}
        <div className="bg-card border-t border-border p-4 flex-shrink-0">
          <Button 
            onClick={handleSave}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11"
            data-testid="save-changes-btn"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

// Live Monitor Panel Component - Full screen on mobile
const LiveMonitorPanel = ({ event, isOpen, onClose }) => {
  const [chatMessages, setChatMessages] = useState(mockChatMessages);
  const [blockedUsers, setBlockedUsers] = useState([]);

  if (!isOpen || !event) return null;

  const handleDeleteMessage = (msgId) => {
    setChatMessages(prev => prev.filter(msg => msg.id !== msgId));
    toast.success('Message deleted');
  };

  const handleBlockUser = (userId, userName) => {
    setBlockedUsers(prev => [...prev, userId]);
    setChatMessages(prev => prev.filter(msg => msg.userId !== userId));
    toast.success(`${userName} has been blocked from chat`);
  };

  const visibleMessages = chatMessages.filter(msg => !blockedUsers.includes(msg.userId));

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="live-monitor-panel">
      {/* Backdrop - visible on desktop */}
      <div className="absolute inset-0 bg-black/60 hidden lg:block" onClick={onClose} />
      
      {/* Panel - Full screen on mobile, side panel on desktop */}
      <div className="relative w-full lg:max-w-lg bg-card lg:border-l border-border h-full overflow-hidden flex flex-col animate-in slide-in-from-right lg:slide-in-from-right slide-in-from-bottom">
        {/* Header */}
        <div className="bg-card border-b border-border p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg" data-testid="close-monitor-panel">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
            <h2 className="text-lg font-semibold text-white">Live Monitor</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-green-400 font-medium">LIVE</span>
          </div>
        </div>

        {/* Event Preview Card */}
        <div className="p-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <img 
              src={event.image} 
              alt={event.title}
              className="w-14 h-10 lg:w-16 lg:h-12 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-white truncate text-sm lg:text-base">{event.title}</h3>
              <p className="text-xs lg:text-sm text-muted-foreground">{formatDate(event.date)}</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-2 lg:gap-3 p-3 lg:p-4 border-b border-border flex-shrink-0">
          <div className="bg-secondary/50 rounded-lg p-2.5 lg:p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Eye className="w-3.5 h-3.5" />
              <span className="text-xs">Current Viewers</span>
            </div>
            <p className="text-lg lg:text-xl font-bold text-white">{mockLiveStats.currentViewers.toLocaleString()}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 lg:p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <BarChart3 className="w-3.5 h-3.5" />
              <span className="text-xs">Peak Viewers</span>
            </div>
            <p className="text-lg lg:text-xl font-bold text-white">{mockLiveStats.peakViewers.toLocaleString()}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 lg:p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <span className="text-xs">💰</span>
              <span className="text-xs">Tips</span>
            </div>
            <p className="text-lg lg:text-xl font-bold text-primary">${mockLiveStats.totalTips.toLocaleString()}</p>
          </div>
          <div className="bg-secondary/50 rounded-lg p-2.5 lg:p-3">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Check className="w-3.5 h-3.5" />
              <span className="text-xs">Checked In</span>
            </div>
            <p className="text-lg lg:text-xl font-bold text-white">{mockLiveStats.ticketsCheckedIn}</p>
          </div>
        </div>

        {/* Chat Section */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex-shrink-0">
            <h3 className="text-sm font-medium text-white">Live Chat</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-2 lg:space-y-3">
            {visibleMessages.map(msg => (
              <div key={msg.id} className="flex items-start gap-2 lg:gap-3 group" data-testid={`chat-msg-${msg.id}`}>
                <img 
                  src={msg.avatar} 
                  alt={msg.userName}
                  className="w-7 h-7 lg:w-8 lg:h-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs lg:text-sm font-medium text-white">{msg.userName}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-xs lg:text-sm text-muted-foreground">{msg.message}</p>
                </div>
                {/* Actions - Always visible on mobile, hover on desktop */}
                <div className="flex items-center gap-0.5 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button 
                    onClick={() => handleDeleteMessage(msg.id)}
                    className="p-1.5 hover:bg-red-500/20 rounded text-muted-foreground hover:text-red-400 transition-colors"
                    title="Delete message"
                    data-testid={`delete-msg-${msg.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                  </button>
                  <button 
                    onClick={() => handleBlockUser(msg.userId, msg.userName)}
                    className="p-1.5 hover:bg-red-500/20 rounded text-muted-foreground hover:text-red-400 transition-colors"
                    title="Block user"
                    data-testid={`block-user-${msg.userId}`}
                  >
                    <Ban className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Data Source Indicator
const AdminDataSourceBadge = () => {
  const source = getDataSource();
  const neonFailed = didNeonFail();
  const errorMessage = getNeonErrorMessage();
  
  return (
    <div className="flex items-center gap-2">
      <div 
        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${
          source === 'neon' 
            ? neonFailed 
              ? 'bg-yellow-500/20 text-yellow-400' 
              : 'bg-green-500/20 text-green-400'
            : 'bg-blue-500/20 text-blue-400'
        }`}
        title={neonFailed ? `Neon failed: ${errorMessage}` : `Data source: ${source}`}
      >
        {source === 'neon' ? (
          neonFailed ? (
            <>
              <AlertCircle className="w-3 h-3" />
              <span>NEON FAILED</span>
            </>
          ) : (
            <>
              <Database className="w-3 h-3" />
              <span>NEON</span>
            </>
          )
        ) : (
          <>
            <Server className="w-3 h-3" />
            <span>MOCK</span>
          </>
        )}
      </div>
    </div>
  );
};

// Main AdminEventList Component
export function AdminEventList() {
  const navigate = useNavigate();
  const [editingEvent, setEditingEvent] = useState(null);
  const [monitoringEvent, setMonitoringEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get current source for dependency tracking
  const searchParams = new URLSearchParams(window.location.search);
  const currentSource = searchParams.get('source') || 'mock';

  // Fetch events on mount and when source changes
  useEffect(() => {
    async function fetchEvents() {
      try {
        setLoading(true);
        setError(null);
        console.log(`[AdminEventList] Fetching events (source=${currentSource})...`);
        
        const response = await getEvents();
        console.log('[AdminEventList] Response:', response);
        
        if (response.success && response.data) {
          // Enrich events with admin-specific fields if not present
          const enrichedEvents = response.data.map(event => ({
            ...event,
            adminStatus: event.status === 'upcoming' ? 'not_started' : 'done',
            ticketsSold: event.ticketsSold || Math.floor(Math.random() * 500),
            totalRevenue: event.totalRevenue || (event.ticketPrice || 50) * Math.floor(Math.random() * 500),
          }));
          setEvents(enrichedEvents);
          
          if (response._fallback) {
            toast.warning('Neon connection failed - showing mock data');
          }
        } else {
          console.error('[AdminEventList] API returned no data');
          setEvents(mockEvents);
          toast.error('Failed to load events');
        }
      } catch (err) {
        console.error('[AdminEventList] Error:', err);
        setError(err.message);
        setEvents(mockEvents);
        toast.error('Failed to load events');
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, [currentSource]);

  const handleEditSave = (formData) => {
    setEvents(prev => prev.map(e => 
      e.id === editingEvent.id 
        ? { ...e, title: formData.title, livestreamEnabled: formData.livestreamEnabled }
        : e
    ));
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white">View active events</h1>
              <AdminDataSourceBadge />
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              Manage your events
              {currentSource === 'neon' && ' • Connected to Neon database'}
            </p>
          </div>
          <Link to="/admin/events/new/basics">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              CREATE NEW EVENT
            </Button>
          </Link>
        </div>

        {/* Neon Failure Warning Banner */}
        {didNeonFail() && currentSource === 'neon' && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 lg:p-4 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div>
                <span className="text-yellow-200 text-sm font-medium">Neon database connection failed</span>
                <p className="text-yellow-200/70 text-xs mt-0.5">{getNeonErrorMessage()} • Showing mock data instead</p>
              </div>
            </div>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Loading events...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card className="bg-red-500/10 border-red-500/30 p-4 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-200 text-sm">{error}</span>
            </div>
          </Card>
        )}

        {/* Content - only show when not loading */}
        {!loading && (
          <>
            {/* Drafts Notice */}
            {mockDrafts && mockDrafts.length > 0 && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 p-3 lg:p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <span className="text-yellow-200 text-sm">You have {mockDrafts.length} unfinished drafts</span>
              </div>
              <Link to="/admin/events/drafts">
                <Button variant="outline" size="sm" className="border-yellow-500/50 text-yellow-200 hover:bg-yellow-500/20">
                  VIEW
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative w-full lg:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search events..." 
              className="bg-card border-border pl-10"
              data-testid="search-events"
            />
          </div>
        </div>

        {/* Events Table/List */}
        <Card className="bg-card border-border overflow-hidden" data-testid="events-table">
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Title</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Date</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Livestream</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Tickets Sold</th>
                  <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Revenue</th>
                  <th className="text-right p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr 
                    key={event.id} 
                    className="border-b border-border hover:bg-secondary/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/events/${event.id}`)}
                    data-testid={`event-row-${event.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                        <span className="font-medium text-white">{event.title}</span>
                      </div>
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {formatDate(event.date)}
                    </td>
                    <td className="p-4">
                      <StatusBadge status={event.adminStatus} />
                    </td>
                    <td className="p-4">
                      <LivestreamBadge enabled={event.livestreamEnabled} />
                    </td>
                    <td className="p-4 text-white">
                      {event.ticketsSold}
                    </td>
                    <td className="p-4 text-primary font-medium">
                      {formatCurrency(event.totalRevenue)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="p-2 hover:bg-primary/20 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                          title="Edit Event"
                          data-testid={`edit-btn-${event.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setMonitoringEvent(event)}
                          className="p-2 hover:bg-primary/20 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                          title="Live Monitor"
                          data-testid={`monitor-btn-${event.id}`}
                        >
                          <Monitor className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/admin/events/${event.id}/analytics`)}
                          className="p-2 hover:bg-primary/20 rounded-lg text-muted-foreground hover:text-primary transition-colors"
                          title="Analytics"
                          data-testid={`analytics-btn-${event.id}`}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile List View */}
          <div className="lg:hidden divide-y divide-border">
            {events.map(event => (
              <div 
                key={event.id} 
                className="p-4 hover:bg-secondary/30 transition-colors"
                data-testid={`event-row-mobile-${event.id}`}
              >
                <div 
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => navigate(`/admin/events/${event.id}`)}
                >
                  <img 
                    src={event.image} 
                    alt={event.title}
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm truncate">{event.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(event.date)}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <StatusBadge status={event.adminStatus} />
                      <LivestreamBadge enabled={event.livestreamEnabled} />
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-muted-foreground">Sold: <span className="text-white">{event.ticketsSold}</span></span>
                      <span className="text-muted-foreground">Revenue: <span className="text-primary font-medium">{formatCurrency(event.totalRevenue)}</span></span>
                    </div>
                  </div>
                </div>
                {/* Mobile Action Buttons */}
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-border/50">
                  <button
                    onClick={() => setEditingEvent(event)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/50 hover:bg-primary/20 rounded-lg text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`edit-btn-mobile-${event.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => setMonitoringEvent(event)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/50 hover:bg-primary/20 rounded-lg text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`monitor-btn-mobile-${event.id}`}
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    Monitor
                  </button>
                  <button
                    onClick={() => navigate(`/admin/events/${event.id}/analytics`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/50 hover:bg-primary/20 rounded-lg text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`analytics-btn-mobile-${event.id}`}
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                    Analytics
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
          </>
        )}
      </div>

      {/* Edit Event Sheet */}
      <EditEventSheet
        event={editingEvent}
        isOpen={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        onSave={handleEditSave}
      />

      {/* Live Monitor Panel */}
      <LiveMonitorPanel
        event={monitoringEvent}
        isOpen={!!monitoringEvent}
        onClose={() => setMonitoringEvent(null)}
      />
    </AdminLayout>
  );
}

export default AdminEventList;
